import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { AuthProvider } from "./auth/AuthProvider";
import { RequireAuth } from "./auth/RequireAuth";
import { StepUpProvider } from "./auth/StepUpProvider";
import type { RuntimeConfig } from "./config";
import { AppLayout } from "./layouts/AppLayout";
import { CallbackPage } from "./pages/CallbackPage";
import { HomePage } from "./pages/HomePage";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PrimitivesPage } from "./pages/PrimitivesPage";
import { AccountPage } from "./pages/account/AccountPage";
import { UsersPage } from "./pages/program/UsersPage";
import { AssignmentsPage } from "./pages/program/AssignmentsPage";
import { usePreferences } from "./lib/format";
import { useAuth } from "./auth/AuthProvider";
import { AuditTrailPage } from "./pages/program/AuditTrailPage";
import { TrainingItemPage } from "./pages/training/TrainingItemPage";
import { TrainingPage } from "./pages/training/TrainingPage";
import { LoadingState } from "./components/ui";

// The docs shelf is its own chunk: the markdown of every document rides with it, not with first paint.
const DocsLayout = lazy(() => import("./pages/docs/DocsLayout").then((m) => ({ default: m.DocsLayout })));
const DocsIndexPage = lazy(() => import("./pages/docs/DocPage").then((m) => ({ default: m.DocsIndexPage })));
const DocPage = lazy(() => import("./pages/docs/DocPage").then((m) => ({ default: m.DocPage })));
const ReleaseNotesPage = lazy(() => import("./pages/ReleaseNotesPage").then((m) => ({ default: m.ReleaseNotesPage })));

/**
 * The server's preferences win over the browser's cache once /v1/me has
 * answered (PLT-03), and the start page (Req 6) is applied once per tab
 * (sessionStorage `lms.landed`) when a sign-in lands on the root.
 */
function PreferencesSync() {
  const { me, status } = useAuth();
  const { update } = usePreferences();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (!me) return;
    update(me.preferences);
  }, [me, update]);
  useEffect(() => {
    if (status !== "signed-in" || !me || location.pathname !== "/") return;
    let landed = false;
    try {
      landed = sessionStorage.getItem("lms.landed") === "1";
      sessionStorage.setItem("lms.landed", "1");
    } catch {
      // no sessionStorage: land on home
    }
    if (landed) return;
    const start = me.preferences.startPage;
    if (start === "program" && me.permissions.includes("user.list")) navigate("/program/users", { replace: true });
    else if (start === "program" && me.permissions.includes("audit_event.list")) navigate("/program/audit", { replace: true });
  }, [status, me, location.pathname, navigate]);
  return null;
}

export function App({ config }: { config: RuntimeConfig }) {
  return (
    <AuthProvider config={config}>
      <StepUpProvider>
        <PreferencesSync />
        <Routes>
          <Route element={<AppLayout config={config} />}>
            <Route index element={<HomePage />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="auth/callback" element={<CallbackPage />} />
            <Route
              path="account"
              element={
                <RequireAuth>
                  <AccountPage />
                </RequireAuth>
              }
            />
            <Route path="account/security" element={<Navigate to="/account#security" replace />} />
            <Route
              path="training"
              element={
                <RequireAuth>
                  <TrainingPage />
                </RequireAuth>
              }
            />
            <Route
              path="training/:id"
              element={
                <RequireAuth>
                  <TrainingItemPage />
                </RequireAuth>
              }
            />
            <Route
              path="program/users"
              element={
                <RequireAuth>
                  <UsersPage />
                </RequireAuth>
              }
            />
            <Route
              path="program/assignments"
              element={
                <RequireAuth>
                  <AssignmentsPage />
                </RequireAuth>
              }
            />
            <Route
              path="program/audit"
              element={
                <RequireAuth>
                  <AuditTrailPage />
                </RequireAuth>
              }
            />
            <Route
              path="docs"
              element={
                <Suspense fallback={<LoadingState label="Loading documentation…" />}>
                  <DocsLayout />
                </Suspense>
              }
            >
              <Route index element={<DocsIndexPage />} />
              <Route path="release-notes" element={<ReleaseNotesPage config={config} />} />
              <Route path=":slug" element={<DocPage />} />
            </Route>
            {config.envName !== "prod" && <Route path="primitives" element={<PrimitivesPage />} />}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </StepUpProvider>
    </AuthProvider>
  );
}
